/* Cart drawer and cart page. Quantity, removal and the order note go through the Cart API; the cart is
   re-rendered by the Section Rendering API so Liquid stays the one source of formatting. There is no
   checkout: the cart ends in the WhatsApp order button. Without JavaScript the cart icon opens the cart
   page, which works as a normal form. Wrapped in a block so nothing leaks into the shared global scope. */
{
  const root = () => window.Shopify?.routes?.root ?? '/';

  /* The WhatsApp cart button waits on window.msCartPending so its message never misses a quantity
     or order note that is still being saved */
  const track = (request) => {
    window.msCartPending = request.catch(() => {});
    return request;
  };

  /* Behaviour shared by the drawer and the page */
  class MsCartBase extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-cart-body]');
      this.status = this.querySelector('[data-status]');
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('submit', (event) => event.preventDefault());
    }

    onClick(event) {
      const step = event.target.closest('[data-step]');
      const remove = event.target.closest('[data-remove]');
      if (!step && !remove) return;
      event.preventDefault();
      const item = event.target.closest('[data-line]');
      if (!item) return;
      const line = Number(item.dataset.line);
      if (remove) return this.change(line, 0, '[data-remove]');
      const input = item.querySelector('[data-qty]');
      this.change(line, this.clamp(input, Number(input.value) + Number(step.dataset.step)), `[data-step="${step.dataset.step}"]`);
    }

    onChange(event) {
      const input = event.target.closest('[data-qty]');
      const note = event.target.closest('[data-note]');
      if (input) {
        const item = input.closest('[data-line]');
        this.change(Number(item.dataset.line), this.clamp(input, Number(input.value)), '[data-qty]');
      } else if (note) {
        this.saveNote(note.value);
      }
    }

    /* Quantity stays between 0 and the units in stock */
    clamp(input, value) {
      const max = input.max === '' ? Infinity : Number(input.max);
      return Math.max(0, Math.min(Number.isFinite(value) ? value : 0, max));
    }

    async change(line, quantity, focusSelector) {
      this.body?.setAttribute('aria-busy', 'true');
      try {
        const response = await track(
          fetch(`${root()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line, quantity }),
          })
        );
        if (!response.ok) throw new Error(String(response.status));
        await this.refresh();
        this.restoreFocus(line, focusSelector);
        this.announce(this.dataset.labelUpdated);
      } catch (error) {
        window.location.reload();
      } finally {
        this.body?.setAttribute('aria-busy', 'false');
      }
    }

    async saveNote(note) {
      try {
        await track(
          fetch(`${root()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note }),
          })
        );
      } catch (error) {
        /* The note is also sent with the WhatsApp message from the live cart; a failed save is not fatal */
      }
    }

    /* Swap in the freshly rendered cart and update every cart count on the page */
    async refresh() {
      const url = `${this.dataset.cartUrl}?section_id=${this.dataset.sectionId}`;
      const [html, cart] = await Promise.all([
        fetch(url).then((response) => response.text()),
        fetch(`${root()}cart.js`).then((response) => response.json()),
      ]);
      const next = new DOMParser().parseFromString(html, 'text/html');
      const fresh = next.querySelector('[data-cart-body]');
      if (fresh && this.body) this.body.innerHTML = fresh.innerHTML;
      const heading = next.querySelector('[data-drawer-count]');
      const own = this.querySelector('[data-drawer-count]');
      if (heading && own) own.textContent = heading.textContent;
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.firstChild.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });
    }

    /* The control that was used is replaced by the swap, so put focus back on its twin */
    restoreFocus(line, selector) {
      const twin = this.body?.querySelector(`[data-line="${line}"] ${selector}`);
      (twin || this.body?.querySelector('.ms-cart__title, .ms-cart__empty a'))?.focus();
    }

    announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = '';
      requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }
  }

  /* Slide-out cart: a native dialog gives the focus trap, Escape and inert page behind it */
  class MsCartDrawer extends MsCartBase {
    connectedCallback() {
      super.connectedCallback();
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.onTrigger = (event) => {
        const icon = event.target.closest('[data-cart-icon]');
        if (!icon || document.querySelector('ms-cart-page')) return;
        event.preventDefault();
        this.open(icon);
      };
      this.onAdd = (event) => {
        event.preventDefault();
        this.refresh().then(() => this.open());
      };
      /* Add to cart from a product card: post to the Cart API, then open the drawer with the new cart */
      this.onCardAdd = async (event) => {
        const form = event.target.closest('form[data-card-add]');
        if (!form) return;
        event.preventDefault();
        const button = form.querySelector('button');
        if (button.getAttribute('aria-disabled') === 'true') return;
        button.setAttribute('aria-disabled', 'true');
        try {
          const response = await track(
            fetch(`${root()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
          );
          const result = await response.json();
          if (!response.ok) {
            this.announce(result.description || result.message || this.dataset.labelError);
            return;
          }
          if (typeof msTrack === 'function') {
            msTrack('add_to_cart', {
              product_id: form.dataset.productId,
              currency: form.dataset.currency,
              value: result.final_price / 100,
            });
          }
          await this.refresh();
          this.open(button);
        } catch (error) {
          this.announce(this.dataset.labelError);
        } finally {
          button.removeAttribute('aria-disabled');
        }
      };
      document.addEventListener('click', this.onTrigger);
      document.addEventListener('cart:add', this.onAdd);
      document.addEventListener('submit', this.onCardAdd);
      this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('ms-scroll-lock');
        this.trigger?.focus();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      document.removeEventListener('cart:add', this.onAdd);
      document.removeEventListener('submit', this.onCardAdd);
    }

    open(trigger) {
      this.trigger = trigger ?? document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('ms-scroll-lock');
    }
  }
  customElements.define('ms-cart-drawer', MsCartDrawer);

  class MsCartPage extends MsCartBase {}
  customElements.define('ms-cart-page', MsCartPage);
}
